import React, { useMemo, useState } from "react";
import { Button, SearchBar } from "../../../components/tailwind";
import { ChatDeleteDialog } from "./ChatDeleteDialog";
import type { ChatSummary } from "./types";

type ChatSelectMenuProps = {
  summaries: ChatSummary[];
  onChatSelect: (chatId: number) => void;
  onChatCreate: () => void;
  onChatDelete: (chatId: number) => void;
  activeChatId?: number;
  className?: string;
};

export const ChatList: React.FC<ChatSelectMenuProps> = ({
  summaries,
  onChatSelect,
  onChatCreate,
  onChatDelete,
  activeChatId,
  className,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [deleteDialogChatId, setDeleteDialogChatId] = useState<number | null>(
    null
  );

  const filteredSummaries = useMemo(() => {
    const q = chatSearchQuery.trim().toLowerCase();
    if (!q) {
      return summaries;
    }
    return summaries.filter((s) => s.name.toLowerCase().includes(q));
  }, [summaries, chatSearchQuery]);

  const handleChatDeleteClick = (
    e: React.MouseEvent,
    chatSummary: ChatSummary
  ): void => {
    e.stopPropagation();
    onChatSelect(chatSummary.id);
    setDeleteDialogChatId(chatSummary.id);
  };

  const handleDeleteConfirm = (chatId: number): void => {
    onChatDelete(chatId);
    setDeleteDialogChatId(null);
  };

  const renderChatLink = (chatSummary: ChatSummary): React.JSX.Element => {
    const isDeleteDialogOpen = deleteDialogChatId === chatSummary.id;

    return (
      <li key={chatSummary.id} className="tw-my-1 tw-h-10">
        <div
          onClick={(e) => {
            e.stopPropagation();
            onChatSelect(chatSummary.id);
            setDeleteDialogChatId(null);
          }}
          className={[
            "tw-transition-colors",
            "tw-group",
            "tw-rounded-md",
            "tw-flex",
            "tw-items-center",
            "tw-justify-between",
            "tw-relative",
            "tw-p-1",
            "hover:tw-bg-gray-100",
            activeChatId === chatSummary.id
              ? "tw-bg-gray-100 hover:!tw-shadow-none hover:!tw-brightness-100"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {!isDeleteDialogOpen ? (
            <>
              <h3
                className={[
                  "tw-mx-3",
                  "tw-my-0",
                  "tw-text-sm",
                  "tw-text-gray-400",
                  "group-hover:tw-text-gray-600",
                  activeChatId === chatSummary.id ? "tw-text-gray-600" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {chatSummary.name}
              </h3>

              <Button
                onClick={(e) => handleChatDeleteClick(e, chatSummary)}
                compact="very"
                borderless
                className="!tw-pl-1"
                variant="transparent"
                data-balloon="Delete Chat"
                data-balloon-pos="right"
                icon
              >
                <i
                  className={[
                    "material-icons",
                    "tw-transition-colors",
                    "tw-text-gray-500/0",
                    "group-hover:tw-text-gray-500/75",
                    "tw-text-xl",
                  ].join(" ")}
                >
                  delete
                </i>
              </Button>
            </>
          ) : (
            <ChatDeleteDialog
              open={isDeleteDialogOpen}
              setOpen={(open) =>
                setDeleteDialogChatId(open ? chatSummary.id : null)
              }
              confirmAction={() => handleDeleteConfirm(chatSummary.id)}
            />
          )}
        </div>
      </li>
    );
  };

  return (
    <nav className={className}>
      <div
        className={[
          "tw-relative",
          "tw-mb-2",
          "tw-flex",
          "tw-flex-col",
          "tw-items-end",
          "tw-gap-2",
        ].join(" ")}
      >
        <button
          className="tw-rounded-lg tw-text-secondary"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-balloon="Toggle Sidebar"
          data-balloon-pos="up-left"
        >
          <i className="material-icons tw-text-2xl">
            {sidebarOpen ? <>menu_open</> : <>menu</>}
          </i>
        </button>
        {!sidebarOpen && (
          <button
            className={[
              "tw-mx-auto",
              "tw-rounded-xl",
              "tw-text-secondary",
            ].join(" ")}
            onClick={onChatCreate}
            data-balloon="Start New Conversation"
            data-balloon-pos="down-left"
          >
            <i className="material-icons tw-text-2xl">add</i>
          </button>
        )}
      </div>
      {sidebarOpen && (
        <div className="tw-w-80 tw-max-w-full">
          <Button
            variant="secondary"
            className="tw-w-full tw-my-2"
            onClick={onChatCreate}
          >
            <div
              className={[
                "tw-flex",
                "tw-justify-center",
                "tw-items-center",
                "tw-gap-2",
              ].join(" ")}
            >
              <span>New chat</span> <i className="material-icons">add</i>
            </div>
          </Button>

          <div className="txu-divider"></div>

          <SearchBar
            id="search-bar"
            className="tw-my-2"
            value={chatSearchQuery}
            onValueChange={setChatSearchQuery}
            placeholder="Search chats..."
            aria-label="Search chats"
          />

          <ul className="tw-my-4 tw-p-0 tw-m-0 tw-list-none">
            {filteredSummaries.map((summary) => renderChatLink(summary))}
          </ul>
        </div>
      )}
    </nav>
  );
};
